// ===========================================
// Enrollment Summary Cards Component
// ===========================================
// Summary statistics for enrollment analytics
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import {
  People,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  Schedule,
} from '@mui/icons-material';
import { EnrollmentSummary } from '../../api/reports.api';
import { formatPercentage } from '../../hooks/useReports';

interface EnrollmentSummaryCardsProps {
  summary?: EnrollmentSummary;
  isLoading: boolean;
}

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
  isLoading: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  color = 'primary.main',
  subtitle,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height={40} />
          {subtitle && <Skeleton variant="text" width="50%" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              bgcolor: color,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const EnrollmentSummaryCards: React.FC<EnrollmentSummaryCardsProps> = ({
  summary,
  isLoading,
}) => {
  const getRetentionColor = (rate: number): string => {
    if (rate >= 85) return '#5cb399'; // Green
    if (rate >= 70) return '#FFCE00'; // Yellow
    return '#FFAE9E'; // Coral/Red
  };

  const retentionRate = summary?.retentionRate ?? 0;
  const retentionColor = getRetentionColor(retentionRate);

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Active Students"
          value={summary?.totalActiveStudents ?? 0}
          icon={<People sx={{ color: 'white' }} />}
          color="primary.main"
          subtitle="Distinct students currently in classes"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Total Active Enrolments"
          value={summary?.totalActiveEnrollments ?? 0}
          icon={<People sx={{ color: 'white' }} />}
          color="primary.main"
          subtitle="Higher than the headcount when a student takes several classes"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="New This Month"
          value={summary?.newEnrollmentsThisMonth ?? 0}
          icon={<TrendingUp sx={{ color: 'white' }} />}
          color="#5cb399"
          subtitle="New enrolments in the last 30 days"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Withdrawals This Month"
          value={summary?.withdrawalsThisMonth ?? 0}
          icon={<TrendingDown sx={{ color: 'white' }} />}
          color={summary && summary.withdrawalsThisMonth > 5 ? '#FFAE9E' : 'primary.main'}
          subtitle="Withdrawals in the last 30 days"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Retention Rate"
          value={summary ? formatPercentage(retentionRate) : '0%'}
          icon={<EmojiEvents sx={{ color: 'white' }} />}
          color={retentionColor}
          subtitle={
            retentionRate >= 85
              ? 'Excellent retention'
              : retentionRate >= 70
              ? 'Good retention'
              : 'Needs improvement'
          }
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Average Duration"
          value={summary ? `${Math.round(summary.averageEnrollmentDuration)} days` : '0 days'}
          icon={<Schedule sx={{ color: 'white' }} />}
          color="primary.main"
          subtitle="Average enrolment length"
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default EnrollmentSummaryCards;
